import { useQuery, useQueryClient } from '@tanstack/react-query';
import { timeOffAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants';

export const usePendingTimeOffCount = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === ROLES.ADMIN;

  const {
    data: pendingCount = 0,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['pendingTimeOffCount'],
    queryFn: () => timeOffAPI.getPendingCount().then(response => response.count),
    enabled: isAdmin, // Only fetch for admin users
    refetchInterval: 30000, // Refetch every 30 seconds
    refetchOnWindowFocus: true,
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Function to manually refresh the count
  const refreshCount = () => {
    queryClient.invalidateQueries({ queryKey: ['pendingTimeOffCount'] });
  };

  // Function to update count optimistically (for immediate UI updates)
  const updateCountOptimistically = (delta: number) => {
    queryClient.setQueryData(['pendingTimeOffCount'], (oldCount: number = 0) => {
      const newCount = Math.max(0, oldCount + delta);
      return newCount;
    });
  };

  return {
    pendingCount,
    isLoading,
    error,
    refetch,
    refreshCount,
    updateCountOptimistically,
    isAdmin
  };
};
